import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useAuthorPosts } from "../context/AuthorPostsContext";
import MyMediaPost from "../pages/MyMediaPost";

export default function UserInfoOverlay({
  open,
  onClose,
  user,
  initialTab = "posts", // "posts" | "media" | "groups"
  mutualGroups = [],
  onOpenGroup,
  myChatId,
  isFollowing,
  onFollow,
  onUnfollow,
  followersCount = 0,
  followingCount = 0,

  // Moment / media posts
  myMediaPosts = [], // media posts already filtered to THIS user
  viewedUser = null,
  currentUser,
  avatarUrl,
  myUserName,
  myDisplayName,
  users = [],
  mediaCommentsByPost = {},
  mediaSocket,
}) {
  const [showUnfollowWarning, setShowUnfollowWarning] = useState(false);

  /* Local mirror of the follow state so the button flips immediately,
     even if the parent list is slow to update. Re-syncs whenever the
     parent sends a new value, the user changes, or the overlay reopens. */
  const [following, setFollowing] = useState(!!isFollowing);
  useEffect(() => {
    setFollowing(!!isFollowing);
  }, [isFollowing, user?.chatId, open]);
  const [showGroups, setShowGroups] = useState(false);
  const [showPosts, setShowPosts] = useState(true);
  const [showMedia, setShowMedia] = useState(false);

  const { openAuthorPosts, closeAuthorPosts, renderAuthorPosts } = useAuthorPosts();

  // Pre-select the requested tab (Posts by default, or whatever the caller
  // asked for via `initialTab`) whenever the overlay opens or switches to a
  // different user — this component may stay mounted with `open` toggling
  // rather than remounting, so local state can otherwise go stale.
  useEffect(() => {
    if (!open) {
      setShowPosts(true);
      setShowMedia(false);
      setShowGroups(false);
      closeAuthorPosts?.();
      return;
    }

    const openToGroups = initialTab === "groups";
    const openToMedia = initialTab === "media";

    setShowGroups(openToGroups);
    setShowMedia(openToMedia);
    setShowPosts(!openToGroups && !openToMedia);

    // Only the Posts tab needs the feed-post author context.
    if (openToGroups || openToMedia) {
      closeAuthorPosts?.();
    } else if (user) {
      openAuthorPosts?.(user);
    }
  }, [open, user?.chatId, initialTab]);

  if (!open || !user) return null;

  const isMe = String(user.chatId) === String(myChatId);

  // For your own profile MyMediaPost shows "my" posts; for anyone else it is
  // told whose posts these are.
  const mediaViewedUser = isMe ? null : viewedUser || user;

  const selectPosts = () => {
    setShowPosts(true);
    setShowMedia(false);
    setShowGroups(false);
    if (user) openAuthorPosts?.(user);
  };

  const selectMedia = () => {
    setShowMedia(true);
    setShowPosts(false);
    setShowGroups(false);
    closeAuthorPosts?.();
  };

  const selectGroups = () => {
    setShowGroups(true);
    setShowPosts(false);
    setShowMedia(false);
    closeAuthorPosts?.();
  };

  const tabLabelClass = (active) =>
    `text-sm font-semibold ${
      active ? "text-blue-500" : "text-neutral-700 dark:text-neutral-300"
    }`;

  return createPortal(
    <>
      {/* BACKDROP */}
      <div
        className="fixed inset-0 z-50 flex justify-end bg-black/50"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        {/* PANEL
            Mobile  : full-width sheet from the right, full height
            Tablet+ : fixed-width sidebar (max-w-sm) from the right
        */}
           <div
          className={[
            "relative flex flex-col",
            "h-full w-full sm:max-w-sm",
            "bg-white dark:bg-neutral-900",
            "shadow-2xl",
            /* safe areas for iOS notch / Dynamic Island */
            "pt-safe-top pb-safe-bottom",
            /* reserve room for the fixed bottom tab bar on mobile only */
            "max-md:pb-[var(--bottom-tabs-h,64px)]",
          ].join(" ")}
          /* stop backdrop-click propagating through the panel */
          onClick={(e) => e.stopPropagation()}
        >
          {/* ── HEADER ── */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200 dark:border-neutral-700 flex-shrink-0">
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
              User Info
            </h2>
            <button
              onClick={onClose}
              className="flex items-center justify-center w-9 h-9 rounded-full text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 active:scale-95 transition-all touch-manipulation text-2xl leading-none"
              aria-label="Close"
            >
              ×
            </button>
          </div>

          {/* ── SCROLLABLE BODY ──
              Always shows the profile (avatar, follow button, stats) — Posts,
              Media and Groups render inline below when toggled, so nothing
              here ever gets hidden or swapped out. */}
          <div
            className="flex-1 overflow-y-auto overscroll-contain"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            {/* AVATAR + NAME + STATUS */}
            <div className="flex flex-col items-center px-6 py-6">
              {/* AVATAR — display only; profile photo changes happen in Settings */}
              <div
                className={[
                  "w-24 h-24 rounded-full",
                  "flex items-center justify-center overflow-hidden",
                  "bg-emerald-600 text-white text-4xl font-bold",
                  "ring-4 ring-white dark:ring-neutral-900 shadow-lg",
                ].join(" ")}
              >
                {user.profileImageUrl ? (
                  <img src={user.profileImageUrl} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <span aria-label="User">👤</span>
                )}
              </div>

              {/* NAME */}
              <div className="mt-4 text-xl font-bold text-neutral-900 dark:text-neutral-100">
                {user.username}
              </div>

                         {/* STATUS */}
              {!isMe && (
                <div className="mt-1 flex items-center gap-1.5 text-sm">
                  <span
                    className={[
                      "w-2 h-2 rounded-full flex-shrink-0",
                      user.online ? "bg-emerald-500" : "bg-neutral-400",
                    ].join(" ")}
                  />
                  <span className={user.online ? "text-emerald-600" : "text-neutral-500 dark:text-neutral-400"}>
                    {user.online ? "Online" : "Offline"}
                  </span>
                </div>
              )}

              {/* FOLLOW / UNFOLLOW */}
              {!isMe && (
                <button
                  onClick={() => {
                    if (following) {
                      setShowUnfollowWarning(true);
                    } else if (onFollow) {
                      onFollow();
                      setFollowing(true);
                    }
                  }}
                  className={[
                    "mt-5 w-full max-w-xs py-2.5 rounded-full font-semibold text-sm transition-all active:scale-95 touch-manipulation",
                    following
                      ? "bg-neutral-100 dark:bg-neutral-800 text-neutral-800 dark:text-neutral-200 hover:bg-neutral-200 dark:hover:bg-neutral-700"
                      : "bg-blue-500 hover:bg-blue-600 active:bg-blue-700 text-white",
                  ].join(" ")}
                  style={{ minHeight: 44 }}
                >
                  {following ? "Following" : "Follow"}
                </button>
              )}

              {/* FOLLOW STATS */}
              <div className="mt-4 flex justify-center gap-8 text-sm text-center">
                <div>
                  <div className="font-bold text-lg text-neutral-900 dark:text-neutral-100">
                    {followersCount}
                  </div>
                  <div className="text-neutral-500 dark:text-neutral-400 text-xs">Followers</div>
                </div>
                <div className="w-px bg-neutral-200 dark:bg-neutral-700" />
                <div>
                  <div className="font-bold text-lg text-neutral-900 dark:text-neutral-100">
                    {followingCount}
                  </div>
                  <div className="text-neutral-500 dark:text-neutral-400 text-xs">Following</div>
                </div>
              </div>

            {/* POSTS / MEDIA / GROUPS TAB BAR */}
            <div className="mt-5 w-full max-w-xs flex justify-center gap-6 border-b border-neutral-200 dark:border-neutral-700 pb-2">
              <button
                onClick={selectPosts}
                className="flex flex-col items-center gap-1 transition-all active:scale-95 touch-manipulation"
                style={{ minHeight: 44 }}
              >
                <span className="text-lg leading-none">📝</span>
                <span className={tabLabelClass(showPosts)}>Posts</span>
              </button>

              <button
                onClick={selectMedia}
                className="flex flex-col items-center gap-1 transition-all active:scale-95 touch-manipulation"
                style={{ minHeight: 44 }}
              >
                <span className="text-lg leading-none">🎬</span>
                <span className={tabLabelClass(showMedia)}>
                  Media{myMediaPosts.length > 0 ? ` (${myMediaPosts.length})` : ""}
                </span>
              </button>

              <button
                onClick={selectGroups}
                className="flex flex-col items-center gap-1 transition-all active:scale-95 touch-manipulation"
                style={{ minHeight: 44 }}
              >
                <span className="text-lg leading-none">👥</span>
                <span className={tabLabelClass(showGroups)}>
                  Groups{mutualGroups.length > 0 ? ` (${mutualGroups.length})` : ""}
                </span>
              </button>
              </div>
            </div>

            {/* AUTHOR POSTS — shown inline, below the profile, when the "Posts" tab is selected */}
            {showPosts && (
              <div className="px-4 pb-4 border-t border-neutral-200 dark:border-neutral-700">
                <div className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 mt-4 mb-3">
                  Posts
                </div>
                {renderAuthorPosts?.()}
              </div>
            )}

            {/* MEDIA — TikTok-style grid of this user's Moment posts, shown when
                the "Media" tab is selected. Tapping a tile opens the full-screen
                viewer (portal'd above this overlay). */}
            {showMedia && (
              <div className="px-4 pb-4 border-t border-neutral-200 dark:border-neutral-700">
                <div className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 mt-4 mb-3">
                  Media
                </div>
                <MyMediaPost
                  embedded
                  myChatId={myChatId}
                  myUserName={myUserName}
                  myDisplayName={myDisplayName}
                  currentUser={currentUser}
                  avatarUrl={avatarUrl}
                  users={users}
                  myMediaPosts={myMediaPosts}
                  viewedUser={mediaViewedUser}
                  mediaCommentsByPost={mediaCommentsByPost}
                  onLikeMediaPost={mediaSocket?.mediaLikePost}
                  onAddMediaComment={mediaSocket?.mediaAddComment}
                  onLikeMediaComment={mediaSocket?.mediaLikeComment}
                  onSupportMediaPost={mediaSocket?.mediaSupportPost}
                  onTrackMediaView={mediaSocket?.mediaTrackView}
                />
              </div>
            )}

            {/* GROUPS — shown only when the "Groups" tab is selected.

                Own profile: groups the user is a member of.

                Other users: groups in common with them. */}

            {showGroups && (

              <div className="px-4 pb-4 border-t border-neutral-200 dark:border-neutral-700">

                <div className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 mt-4 mb-3">

                  {isMe ? "Groups" : "Groups in common"}

                </div>



                {mutualGroups.length === 0 ? (

                  <div className="text-sm text-neutral-400 dark:text-neutral-500 text-center py-2">

                    {isMe ? "You're not in any groups yet" : "No groups in common"}

                  </div>

                ) : (

                  <div className="flex flex-col gap-2">

                 {mutualGroups.map((g) => (
<button
  key={g.groupId}
  onClick={() => { onOpenGroup?.(g); onClose?.(); }}
  className="w-full px-4 py-3 rounded-xl bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700 active:scale-[0.98] transition-all touch-manipulation"
  style={{ minHeight: 44 }}
>
  <div className="flex items-center justify-between">
    <span className="font-medium text-neutral-900 dark:text-neutral-100">
      {g.name}
    </span>

    <span className="text-xs text-neutral-500 dark:text-neutral-400">
      {g.memberCount ?? g.members?.length ?? 0}
    </span>
  </div>
</button>
))}

                  </div>

                )}

              </div>

            )}



            {/* bottom breathing room above footer */}

            <div className="h-4" />

          </div>



                 {/* ── FOOTER — always pinned to bottom (hidden on your own profile) ── */}
          {!isMe && (
            <div className="flex-shrink-0 px-4 py-3 border-t border-neutral-200 dark:border-neutral-700 flex gap-3"
              style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)" }}
            >
              <button
                className="flex-1 py-2.5 rounded-full bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 font-medium text-sm active:scale-95 transition-all touch-manipulation"
                style={{ minHeight: 44 }}
              >
                🚫 Block
              </button>
              <button
                className="flex-1 py-2.5 rounded-full bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 font-medium text-sm active:scale-95 transition-all touch-manipulation"
                style={{ minHeight: 44 }}
              >
                ⚠️ Report
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── UNFOLLOW WARNING MODAL ──
          Rendered as a top-level sibling of the backdrop above. Combined
          with the createPortal() wrapping the whole return, this entire
          overlay (backdrop, panel, and this modal) mounts directly under
          <body> — completely outside React's normal render tree. That
          means no ancestor anywhere (ChatWindow's position:fixed + z-0
          wrapper, any future transform/filter/isolate wrapper, etc.) can
          ever trap it inside a lower stacking context again. Its z-50 /
          z-[70] now always compare directly against top-level siblings
          like the mobile bottom tabs bar (z-[60]). */}
           {showUnfollowWarning && (
        <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/60 px-0 sm:px-4">
          <div
            className="bg-white dark:bg-neutral-900 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:w-[min(420px,calc(100vw-32px))] sm:max-w-sm p-6 text-center"
            style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 24px)" }}
          >
            <div className="text-xl font-bold text-neutral-900 dark:text-neutral-100 mb-2">
              Unfollow {user.username}?
            </div>
            <div className="text-sm text-neutral-500 dark:text-neutral-400 mb-6 leading-relaxed">
              This action will permanently delete your chat history and all shared media with this user.{" "}
              <span className="text-red-500 font-medium">This cannot be undone.</span>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowUnfollowWarning(false)}
                className="flex-1 py-3 rounded-full bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700 font-semibold text-sm active:scale-95 transition-all touch-manipulation"
                style={{ minHeight: 44 }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowUnfollowWarning(false);
                  if (onUnfollow) {
                    onUnfollow();
                    setFollowing(false);
                  }
                }}
                className="flex-1 py-3 rounded-full bg-red-500 hover:bg-red-600 text-white font-semibold text-sm active:scale-95 transition-all touch-manipulation"
                style={{ minHeight: 44 }}
              >
                Unfollow
              </button>
            </div>
          </div>
        </div>
      )}
    </>,
    document.body
  );
}